/**
 * TrialGateBanner.jsx — displays the current 1-day free-trial state
 * for a gated feature (Learn / My Library / Modules).
 *
 *   • kind = "premium"  → no banner (unlimited access).
 *   • kind = "trial"    → sky-blue strip showing time remaining +
 *                         "Upgrade to Premium" CTA.
 *   • kind = "expired"  → full paywall block, blocks the page content
 *                         via `renderWhenExpired` (parent decides).
 *
 * Consumers can pass `renderPaywallOnly` to skip the trial pill and
 * only render the paywall on expiry — useful when the parent renders
 * a soft banner elsewhere.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Lock, Crown, Clock, Moon } from 'lucide-react';

const fmtHrs = (secs) => {
  if (!secs || secs <= 0) return '0h';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h <= 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const fmtMinsSecs = (secs) => {
  if (!secs || secs <= 0) return '0m';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m === 0) return `${s}s`;
  if (s === 0) return `${m}m`;
  return `${m}m ${s}s`;
};

const FEATURE_LABEL = {
  learn: 'Learn',
  my_library: 'My Library',
  modules: 'Reader Modules',
};

export const TrialGateBanner = ({ status, feature, renderPaywallOnly = false }) => {
  if (!status) return null;
  const label = FEATURE_LABEL[feature] || 'this feature';

  // Feb 14 2026 — LEARN uses a per-day 20-min budget. When today's
  // minutes are gone but the 7-day window still has room, show a
  // "come back tomorrow" state instead of the terminal paywall.
  if (status.kind === 'daily_exhausted') {
    return (
      <div
        className="rounded-2xl border border-indigo-400/40 bg-gradient-to-br from-indigo-950/60 via-slate-900 to-slate-950 p-5 mb-5"
        data-testid={`trial-gate-daily-exhausted-${feature}`}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-400/20 border border-indigo-400/40 grid place-items-center flex-shrink-0">
            <Moon className="w-5 h-5 text-indigo-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="eyebrow text-indigo-300 mb-1">Today&apos;s 20 min are done</p>
            <h3 className="font-display text-xl font-black text-white tracking-tight mb-1">
              Come back tomorrow — <span className="text-indigo-300">{status.days_left ?? 0} day{(status.days_left ?? 0) === 1 ? '' : 's'} left</span>
            </h3>
            <p className="text-sm text-white/70 leading-relaxed">
              Free readers get 20 minutes of Learn per day for 7 days. Or go Premium for unlimited training.
            </p>
          </div>
        </div>
        <Link
          to="/premium"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-black font-black py-3 rounded-full uppercase tracking-widest text-xs"
          data-testid={`trial-gate-upgrade-daily-${feature}`}
        >
          <Crown className="w-4 h-4" /> Unlock unlimited
        </Link>
      </div>
    );
  }

  if (status.kind === 'expired') {
    return (
      <div
        className="rounded-2xl border border-amber-400/50 bg-gradient-to-br from-amber-950/60 via-slate-900 to-slate-950 p-5 mb-5"
        data-testid={`trial-gate-expired-${feature}`}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-400/20 border border-amber-400/40 grid place-items-center flex-shrink-0">
            <Lock className="w-5 h-5 text-amber-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="eyebrow text-amber-300 mb-1">Your free trial ended</p>
            <h3 className="font-display text-xl font-black text-white tracking-tight mb-1">
              Unlock {label} with Premium
            </h3>
            <p className="text-sm text-white/70 leading-relaxed">
              {feature === 'learn'
                ? "You've used all 7 free trial days. Go Premium to keep training your reading — unlimited every day."
                : "You've used your 24 hours of free access. Go Premium to keep saving personal docs and unlocking every module."}
            </p>
          </div>
        </div>
        <Link
          to="/premium"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-black font-black py-3 rounded-full uppercase tracking-widest text-xs"
          data-testid={`trial-gate-upgrade-${feature}`}
        >
          <Crown className="w-4 h-4" /> Upgrade to Premium
        </Link>
      </div>
    );
  }

  if (renderPaywallOnly) return null;
  if (status.kind === 'premium') return null;

  // Trial-active banner. For LEARN, show today's minutes remaining
  // + how many days are left in the 7-day window. For other features
  // show hours remaining in the 24h window.
  const isLearn = feature === 'learn';
  return (
    <div
      className="rounded-xl border border-cyan-500/30 bg-cyan-500/5 px-3.5 py-2.5 mb-4 flex items-center gap-3"
      data-testid={`trial-gate-active-${feature}`}
    >
      <div className="w-8 h-8 rounded-lg bg-cyan-500/15 border border-cyan-400/30 grid place-items-center flex-shrink-0">
        <Clock className="w-4 h-4 text-cyan-300" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-widest text-cyan-300 font-black flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Free trial · {label}
        </p>
        <p className="text-xs text-white/70">
          <span className="font-black text-cyan-200 tabular-nums" data-testid={`trial-gate-remaining-${feature}`}>
            {isLearn ? fmtMinsSecs(status.today_left ?? status.seconds_left) : fmtHrs(status.seconds_left)}
          </span>{' '}
          {isLearn ? (
            <>left today · {status.days_left ?? 0} day{(status.days_left ?? 0) === 1 ? '' : 's'} in trial.</>
          ) : (
            <>left. After that, Premium unlocks everything.</>
          )}
        </p>
      </div>
      <Link
        to="/premium"
        className="hidden sm:inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-amber-300 hover:text-amber-200 font-black"
        data-testid={`trial-gate-upgrade-inline-${feature}`}
      >
        <Crown className="w-3.5 h-3.5" /> Upgrade
      </Link>
    </div>
  );
};

export default TrialGateBanner;
